import { Aeonix_MERN_Industrial_Training_Letter_Redacted } from "../assets";
import { appreciations } from "../content";
import type { Appreciation } from "../types/content";

/**
 * ============================================================================
 * Appreciation id → the scanned document. A TWO-KEY PUBLISH GATE (D-106).
 * ============================================================================
 *
 * Modelled on `workshopImages.ts` rather than on `awardImages.ts`, and the
 * difference is which failure each one can have.
 *
 * `awardImages.ts` is PARTIAL by design: two of the four awards are podium
 * finishes and a placement has no certificate, so absence is the normal state
 * and only one direction can be asserted there. Here absence is not a state at
 * all — an appreciation with no document is a testimonial the site wrote for
 * itself, which the owner's standing instruction forbids, and the type makes
 * `image` required so it cannot arrive without one. That means BOTH directions
 * are checkable at module load, and both are checked:
 *
 *   · `status: "published"` with no binary — the layer says it may be shown and
 *     nothing exports it, so the card renders no evidence and looks finished.
 *     This is D-065 exactly: the gate answers "may this be shown?", the render
 *     answers "is it shown?", and they live in two files.
 *   · a binary under a status that is NOT published — the reverse, and the
 *     worse one: an unredacted or uncleared scan reachable from the bundle
 *     because someone added the import before the ruling landed.
 *
 * Both throw here, which fails `npm run build` because the pre-render executes
 * the component tree. Neither is a lint rule anyone can forget to run.
 *
 * THE BINARY IS D-079'S VERIFIED LETTERHEAD-ONLY DERIVATIVE and its original is
 * not in this tree, so this module cannot be edited into showing an unredacted
 * scan — the file it would need is not importable. `src/assets/index.ts`
 * carries the redaction record and the reason the export was retired in D-087
 * and restored here.
 */

/** Appreciation image id -> imported binary. */
const APPRECIATION_IMAGES: Readonly<Record<string, string>> = {
	"aeonix-industrial-training-letter": Aeonix_MERN_Industrial_Training_Letter_Redacted
};

/** The binary for an image id, or undefined if none is exported. */
export const appreciationImage = (id: string): string | undefined => APPRECIATION_IMAGES[id];

/* ----------------------------------------------------- the two-key gate --- */
const declaredIds = new Set<string>();
for (const appreciation of appreciations) {
	const { image } = appreciation;
	declaredIds.add(image.id);
	const binary: string | undefined = APPRECIATION_IMAGES[image.id];
	if (image.status === "published" && binary === undefined) {
		throw new Error(
			`appreciationImages: "${image.id}" is marked published in appreciations.json but is exported from ` +
				"no module, so the card would render its prose with no document behind it. Add the export, or " +
				"change the status to record that the scan is not cleared yet."
		);
	}
	if (image.status !== "published" && binary !== undefined) {
		throw new Error(
			`appreciationImages: "${image.id}" has a binary here but status "${image.status}" in ` +
				"appreciations.json. An uncleared scan that something imports is reachable from the bundle " +
				"whether or not a page renders it (D-018)."
		);
	}
}

for (const key of Object.keys(APPRECIATION_IMAGES)) {
	if (!declaredIds.has(key)) {
		throw new Error(
			`appreciationImages: "${key}" is exported here but names no image in appreciations.json. ` +
				"Naming a binary emits it into build/assets whether or not anything renders it — a re-export " +
				"is not inert (D-018/D-087)."
		);
	}
}

/** Every appreciation whose document is cleared AND exported. The page's list. */
export const publishableAppreciations: Appreciation[] = appreciations.filter(
	(appreciation: Appreciation) =>
		appreciation.image.status === "published" && APPRECIATION_IMAGES[appreciation.image.id] !== undefined
);
